import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Calendar, GraduationCap, FileText, Building2, BookOpen, Upload, CheckCircle2, ChevronRight, MapPin, ShieldAlert, Users as UsersIcon, Globe2, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { friendlyUploadError } from '../lib/errors';
import { compressImage } from '../lib/imageCompression';
import COUNTRIES from '../lib/countries';
import { uploadWithProgress } from '../lib/uploadWithProgress';

/**
 * Reusable student-profile creation form.
 * - Default mode (recruiter): used at /create-student route.
 * - Admin mode (`adminMode={true}`): posts to /api/admin/students, shows a
 *   recruiter attribution selector, and invokes onSuccess() instead of
 *   navigating away.
 */
const CreateStudentProfile = ({
  adminMode = false,
  onSuccess = null,
  onCancel = null,
  prefill = null,           // optional { student_name, email, phone, dob }
  prefillRecruiterId = '',  // optional — e.g. 'DIRECT_SOCIAL_LEADS' when converting a Meta/Google lead
  sourceLeadId = '',        // optional — back-link the new student to the lead it came from
}) => {
  const navigate = useNavigate();
  const [colleges, setColleges] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    student_name: prefill?.student_name || '',
    email: prefill?.email || '',
    phone: prefill?.phone || '',
    dob: prefill?.dob || '',
    high_school_country: '',
    high_school_country_other: '',
    qualifications: '',
    recruiter_id: prefillRecruiterId || '',
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_phone: '',
    marital_status: '',
    student_status: '',
  });

  const [documents, setDocuments] = useState({
    class_12_marksheet: null,
    class_10_marksheet: null,
    id_proof_primary: null,
    id_proof_secondary: null,
    photo: null,
    english_proficiency_test: null,
    diploma_transcript: null,
    study_permit: null,
  });
  // Optional extra files the admin may want to attach (transcripts, IELTS, financials, etc.)
  // Each entry: { id: string, file: File }
  const [additionalDocs, setAdditionalDocs] = useState([]);

  const [selectedColleges, setSelectedColleges] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [collegeQuery, setCollegeQuery] = useState('');

  useEffect(() => {
    fetchColleges();
    if (adminMode) fetchRecruiters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchColleges = async () => {
    try {
      const url = adminMode
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges`
        : `${process.env.REACT_APP_BACKEND_URL}/api/recruiter/colleges`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      setColleges(data.colleges || []);
    } catch (err) {
      console.error('Failed to fetch colleges:', err);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/recruiters`,
        { credentials: 'include' },
      );
      if (!response.ok) return;
      const data = await response.json();
      // Show every registered recruiter so admins can attribute walk-ins regardless
      // of onboarding status. Sort active first, then alphabetically for usability.
      const all = data.recruiters || [];
      all.sort((a, b) => {
        const aActive = a.status === 'active' ? 0 : 1;
        const bActive = b.status === 'active' ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        return (a.company_name || '').localeCompare(b.company_name || '');
      });
      setRecruiters(all);
    } catch (err) {
      console.error('Failed to fetch recruiters:', err);
    }
  };

  const handleFileChange = (key, file) => setDocuments({ ...documents, [key]: file });
  const handleCollegeToggle = (id) =>
    setSelectedColleges((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const handleProgramToggle = (program) =>
    setSelectedPrograms((prev) => (prev.includes(program) ? prev.filter((x) => x !== program) : [...prev, program]));

  const getAvailablePrograms = () => {
    const all = colleges
      .filter((c) => selectedColleges.includes(c.id))
      .flatMap((c) => (c.programs || []).map((p) => p.program_name || p));
    return Array.from(new Set(all));
  };

  const [reviewOpen, setReviewOpen] = useState(false);

  // Recruiter flow: submit button opens a review dialog first so the recruiter
  // can double-check every field + document before the profile is created.
  // Admin flow keeps the old direct submit for speed at walk-ins.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!adminMode) {
      // Basic validation before showing review.
      if (!formData.marital_status) {
        setError('Please select the student\u2019s marital status.');
        return;
      }
      if (!formData.student_status) {
        setError('Please select Domestic or International student status.');
        return;
      }
      const isInternational = formData.student_status === 'international';
      const requiredDocs = docMeta.filter((d) =>
        !d.optional || (isInternational && d.key === 'english_proficiency_test')
      );
      const missing = requiredDocs.filter((d) => !documents[d.key]).map((d) => d.key);
      if (missing.length > 0) {
        setError(`Please upload all ${requiredDocs.length} required documents (missing ${missing.length}).`);
        return;
      }
      setReviewOpen(true);
      return;
    }
    await performSubmit();
  };

  const performSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      // Compress images on-device to keep upload under typical ingress timeouts.
      setUploadProgress('compressing');
      // Only compress docs that belong to the current required set
      // (study_permit + english_proficiency_test are skipped for domestic).
      const requiredKeys = new Set(docMeta.map((d) => d.key));
      const compressedDocs = await Promise.all(
        Object.entries(documents)
          .filter(([k, f]) => !!f && requiredKeys.has(k))
          .map(async ([k, f]) => [k, await compressImage(f)])
      );

      const data = new FormData();
      data.append('student_name', formData.student_name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('dob', formData.dob);
      // High school country — send the resolved value (handles "Other" → custom typed value).
      const hsCountry = formData.high_school_country === 'Other'
        ? (formData.high_school_country_other || '').trim()
        : formData.high_school_country;
      if (hsCountry) data.append('high_school_country', hsCountry);
      data.append('qualifications', JSON.stringify({ summary: formData.qualifications }));
      // Emergency contact (optional but encouraged).
      if (formData.emergency_contact_name.trim()) data.append('emergency_contact_name', formData.emergency_contact_name.trim());
      if (formData.emergency_contact_relation.trim()) data.append('emergency_contact_relation', formData.emergency_contact_relation.trim());
      if (formData.emergency_contact_phone.trim()) data.append('emergency_contact_phone', formData.emergency_contact_phone.trim());
      if (formData.marital_status) data.append('marital_status', formData.marital_status);
      if (formData.student_status) data.append('student_status', formData.student_status);
      data.append('preferred_colleges', JSON.stringify(selectedColleges));
      data.append('preferred_programs', JSON.stringify(selectedPrograms));
      if (adminMode && formData.recruiter_id) data.append('recruiter_id', formData.recruiter_id);
      if (adminMode && sourceLeadId) {
        data.append('source_lead_id', sourceLeadId);
        // If converting a social/ad lead, also tag the source explicitly so
        // reporting can break down by funnel even when no recruiter is set.
        if (formData.recruiter_id === 'DIRECT_SOCIAL_LEADS') {
          data.append('attribution_source', 'social_lead');
        }
      }
      // For admin mode, docs are optional → only append the ones actually
      // selected. For recruiter/self-signup flows we still send all (backend
      // still requires them on those endpoints).
      compressedDocs.forEach(([k, f]) => {
        if (adminMode && !f) return;
        data.append(k, f);
      });
      // Attach all optional additional documents under one repeated form field.
      additionalDocs.forEach(({ file }) => {
        if (file) data.append('additional_documents', file, file.name);
      });

      const url = adminMode
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/students`
        : `${process.env.REACT_APP_BACKEND_URL}/api/recruiter/students`;
      const response = await uploadWithProgress(url, {
        method: 'POST',
        body: data,
        credentials: 'include',
        onProgress: ({ pct }) => setUploadProgress(pct ?? 'uploading'),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.detail || 'Could not create the student profile.');
      if (onSuccess) onSuccess(body); else navigate('/recruiter-dashboard');
    } catch (err) {
      setError(friendlyUploadError(err, 'Network error. Please retry.'));
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const docMeta = [
    { key: 'class_12_marksheet', label: 'Class 12 Marksheet' },
    { key: 'class_10_marksheet', label: 'Class 10 Marksheet' },
    { key: 'id_proof_primary', label: 'Student ID 1 (PR Card / Passport)' },
    { key: 'id_proof_secondary', label: 'Student ID 2 (Driving License or BCeID)' },
    { key: 'photo', label: 'Student Photograph' },
    // Optional for ALL students — admins can add later from the edit dialog.
    { key: 'english_proficiency_test', label: 'English Proficiency Proof (IELTS / TOEFL / PTE / Duolingo)', optional: true },
    { key: 'diploma_transcript', label: 'Diploma / Degree Transcript', optional: true },
    // International-only — Study Permit / Work Permit becomes required when
    // student_status === 'international'. The english_proficiency proof above
    // also gets marked required for international students at validation time.
    ...(formData.student_status === 'international'
      ? [{ key: 'study_permit', label: 'Study Permit / Work Permit' }]
      : []),
  ];

  const uploadedCount = docMeta.filter((d) => !!documents[d.key]).length;
  const progress = [
    formData.student_name && formData.email && formData.phone && formData.dob,
    formData.qualifications,
    adminMode ? true : uploadedCount === docMeta.length,
    selectedColleges.length > 0,
  ].filter(Boolean).length;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-student-form">
      {/* Step progress strip */}
      <div className="flex items-center gap-1.5" aria-label="Form progress">
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className={`h-1.5 flex-1 rounded-full transition-colors ${n <= progress ? 'bg-tge-gold' : 'bg-tge-cream'}`}
          />
        ))}
        <span className="text-[11px] text-tge-muted font-semibold ml-2 shrink-0">{progress}/4 sections</span>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm" data-testid="create-student-error">
          {error}
        </div>
      )}

      {/* Optional recruiter attribution (admin only) */}
      {adminMode && (
        <Section
          icon={Building2}
          title="Attribute to recruiter (optional)"
          subtitle="Pick a recruiter to credit them with this student — commission flow stays intact. Leave blank for direct walk-ins."
          accent
        >
          <select
            value={formData.recruiter_id}
            onChange={(e) => setFormData({ ...formData, recruiter_id: e.target.value })}
            className="w-full h-11 rounded-xl border border-tge-navy/15 bg-white px-3 text-sm focus:outline-none focus:border-tge-gold focus:ring-2 focus:ring-tge-gold/20 transition"
            data-testid="admin-student-recruiter-select"
          >
            <option value="">— Direct walk-in (no recruiter) —</option>
            <option value="DIRECT_SOCIAL_LEADS">🌐 Direct social leads (Meta / Google / Instagram ad)</option>
            {recruiters.map((r) => {
              const statusTag = r.status === 'active'
                ? ''
                : r.status === 'agreement_sent'
                ? '  ⚠ pending signature'
                : r.status === 'pending'
                ? '  ⚠ not yet onboarded'
                : `  · ${r.status}`;
              return (
                <option key={r.recruiter_id} value={r.recruiter_id}>
                  {r.company_name || 'Unnamed agency'} · {r.contact_email || r.contact_name || r.recruiter_id}{statusTag}
                </option>
              );
            })}
          </select>
          {formData.recruiter_id === 'DIRECT_SOCIAL_LEADS' && (
            <p className="text-xs text-tge-muted mt-2">
              This student is tagged as a <strong>direct social ad lead</strong>. No recruiter commission applies; reporting will group them under <em>Direct social leads</em>.
            </p>
          )}
          {formData.recruiter_id && formData.recruiter_id !== 'DIRECT_SOCIAL_LEADS' && (
            <p className="text-xs text-tge-muted mt-2">
              Future commissions on this student will flow to <strong>{(recruiters.find((r) => r.recruiter_id === formData.recruiter_id) || {}).company_name || 'this recruiter'}</strong>. They will see the application in their portal immediately.
            </p>
          )}
        </Section>
      )}

      {/* 1. Student basic info */}
      <Section icon={User} title="Student information" subtitle="Personal details for the application.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldGroup icon={User} label="Full name *">
            <Input required value={formData.student_name} onChange={(e) => setFormData({ ...formData, student_name: e.target.value })} placeholder="Jane Doe" data-testid="create-student-name" />
          </FieldGroup>
          <FieldGroup icon={Mail} label="Email *">
            <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="student@example.com" data-testid="create-student-email" />
          </FieldGroup>
          <FieldGroup icon={Phone} label="Phone *">
            <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 (555) 000-0000" data-testid="create-student-phone" />
          </FieldGroup>
          <FieldGroup icon={Calendar} label="Date of birth *">
            <Input type="date" required value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} data-testid="create-student-dob" />
          </FieldGroup>
          <FieldGroup icon={MapPin} label="Country where you completed high school *">
            <select
              required
              value={formData.high_school_country}
              onChange={(e) => setFormData({ ...formData, high_school_country: e.target.value })}
              className="w-full h-11 rounded-xl border border-tge-navy/15 bg-white px-3 text-sm focus:outline-none focus:border-tge-gold focus:ring-2 focus:ring-tge-gold/20 transition"
              data-testid="create-student-hs-country"
            >
              <option value="">Select country…</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FieldGroup>
          {formData.high_school_country === 'Other' && (
            <FieldGroup icon={MapPin} label="Specify country *">
              <Input
                required
                value={formData.high_school_country_other}
                onChange={(e) => setFormData({ ...formData, high_school_country_other: e.target.value })}
                placeholder="Enter country name"
                data-testid="create-student-hs-country-other"
              />
            </FieldGroup>
          )}
        </div>
      </Section>

      {/* 1b. Emergency contact */}
      <Section icon={ShieldAlert} title="Emergency contact" subtitle="Someone we should contact in case of an emergency (not the student themselves).">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FieldGroup icon={User} label="Full name">
            <Input
              value={formData.emergency_contact_name}
              onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
              placeholder="e.g. Robert Doe"
              data-testid="create-student-emergency-name"
            />
          </FieldGroup>
          <FieldGroup icon={UsersIcon} label="Relation">
            <select
              value={formData.emergency_contact_relation}
              onChange={(e) => setFormData({ ...formData, emergency_contact_relation: e.target.value })}
              className="w-full h-11 rounded-xl border border-tge-navy/15 bg-white px-3 text-sm focus:outline-none focus:border-tge-gold focus:ring-2 focus:ring-tge-gold/20 transition"
              data-testid="create-student-emergency-relation"
            >
              <option value="">Select relation…</option>
              <option value="Parent">Parent</option>
              <option value="Father">Father</option>
              <option value="Mother">Mother</option>
              <option value="Guardian">Guardian</option>
              <option value="Sibling">Sibling</option>
              <option value="Spouse">Spouse</option>
              <option value="Friend">Friend</option>
              <option value="Relative">Relative</option>
              <option value="Other">Other</option>
            </select>
          </FieldGroup>
          <FieldGroup icon={Phone} label="Contact number">
            <Input
              type="tel"
              value={formData.emergency_contact_phone}
              onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
              placeholder="+1 (555) 000-0000"
              data-testid="create-student-emergency-phone"
            />
          </FieldGroup>
        </div>
      </Section>

      {/* 1c. Marital & residency status */}
      <Section icon={Globe2} title="Marital status & residency" subtitle="Required to determine which documents the student must submit.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldGroup icon={Heart} label={adminMode ? 'Marital status' : 'Marital status *'}>
            <select
              required={!adminMode}
              value={formData.marital_status}
              onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
              className="w-full h-11 rounded-xl border border-tge-navy/15 bg-white px-3 text-sm focus:outline-none focus:border-tge-gold focus:ring-2 focus:ring-tge-gold/20 transition"
              data-testid="create-student-marital-status"
            >
              <option value="">Select marital status…</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
              <option value="Separated">Separated</option>
              <option value="Other">Other</option>
            </select>
          </FieldGroup>
          <FieldGroup icon={Globe2} label={adminMode ? 'Student status' : 'Student status *'}>
            <select
              required={!adminMode}
              value={formData.student_status}
              onChange={(e) => setFormData({ ...formData, student_status: e.target.value })}
              className="w-full h-11 rounded-xl border border-tge-navy/15 bg-white px-3 text-sm focus:outline-none focus:border-tge-gold focus:ring-2 focus:ring-tge-gold/20 transition"
              data-testid="create-student-status"
            >
              <option value="">Select status…</option>
              <option value="domestic">Domestic</option>
              <option value="international">International</option>
            </select>
          </FieldGroup>
        </div>
        {formData.student_status === 'international' && (
          <div className="mt-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5" data-testid="international-docs-notice">
            International students must additionally upload a <strong>Study Permit / Work Permit</strong> and an
            <strong> English Proficiency Test</strong> document below.
          </div>
        )}
      </Section>

      {/* 2. Academic background */}
      <Section icon={GraduationCap} title="Academic background" subtitle="Grades, achievements, awards, English test scores.">
        <Textarea
          required
          value={formData.qualifications}
          onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
          placeholder="Class 10: 88% &middot; Class 12: 79% &middot; IELTS 6.5 (overall) &middot; school honours..."
          rows={4}
          className="rounded-xl"
          data-testid="create-student-qualifications"
        />
      </Section>

      {/* 3. Documents */}
      <Section
        icon={Upload}
        title={adminMode ? 'Student documents (optional)' : 'Required documents'}
        subtitle={adminMode
          ? `Upload now or attach later from the student profile. Accepted: PDF / JPG / PNG, up to 10 MB each.`
          : `All ${docMeta.length} are required. Accepted: PDF / JPG / PNG, up to 10 MB each.`}
        right={<UploadedPill done={uploadedCount} total={docMeta.length} />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {docMeta.map((doc) => (
            <DocSlot
              key={doc.key}
              label={doc.optional ? `${doc.label} (optional)` : doc.label}
              file={documents[doc.key]}
              onChange={(f) => handleFileChange(doc.key, f)}
              testid={`create-student-doc-${doc.key}`}
            />
          ))}
        </div>
      </Section>

      {/* 4. Preferred colleges */}
      <Section icon={Building2} title="Preferred colleges" subtitle="Tick the colleges this student wants to apply to.">
        {colleges.length === 0 ? (
          <div className="text-center py-8 text-sm text-tge-muted bg-tge-cream-soft/40 rounded-xl border border-dashed border-tge-navy/15">
            No colleges available yet. Ask an admin to add one in <em>Colleges</em>.
          </div>
        ) : (
          <>
            <div className="mb-3 relative">
              <input
                type="text"
                value={collegeQuery}
                onChange={(e) => setCollegeQuery(e.target.value)}
                placeholder="Search colleges by name, city, or province…"
                className="w-full pl-3 pr-3 h-10 text-sm rounded-xl border border-tge-navy/15 focus:outline-none focus:border-tge-gold focus:ring-2 focus:ring-tge-gold/20 transition"
                data-testid="create-student-college-search"
              />
            </div>
            {(() => {
              // Group colleges by province (or "Other" when unset). Also apply
              // the search query across name + city + province.
              const q = collegeQuery.trim().toLowerCase();
              const filtered = colleges.filter((c) =>
                !q ||
                (c.name || '').toLowerCase().includes(q) ||
                (c.city || '').toLowerCase().includes(q) ||
                (c.province || '').toLowerCase().includes(q)
              );
              const groups = new Map();
              filtered.forEach((c) => {
                const key = (c.province || 'Other').trim() || 'Other';
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(c);
              });
              const sortedProvinces = [...groups.keys()].sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b));
              if (filtered.length === 0) {
                return <div className="text-center py-8 text-sm text-tge-muted">No colleges match &quot;{collegeQuery}&quot;.</div>;
              }
              return sortedProvinces.map((province) => (
                <div key={province} className="mb-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-tge-navy/50 mb-2 pl-1">
                    {province} <span className="text-tge-navy/30 font-normal">({groups.get(province).length})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {groups.get(province).map((college) => (
                      <PickableRow
                        key={college.id}
                        checked={selectedColleges.includes(college.id)}
                        onChange={() => handleCollegeToggle(college.id)}
                        title={college.name}
                        subtitle={`${college.city ? college.city + ' · ' : ''}${college.programs?.length || 0} programs`}
                        testid={`create-student-college-${college.id}`}
                      />
                    ))}
                  </div>
                </div>
              ));
            })()}
          </>
        )}
      </Section>

      {/* 5. Preferred programs (only when at least one college is picked) */}
      {selectedColleges.length > 0 && (
        <Section icon={BookOpen} title="Preferred programs" subtitle="Pick one or more programs across the chosen colleges.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {getAvailablePrograms().map((program) => (
              <PickableRow
                key={program}
                checked={selectedPrograms.includes(program)}
                onChange={() => handleProgramToggle(program)}
                title={program}
                testid={`create-student-program-${program.replace(/\s+/g, '-').toLowerCase()}`}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Sticky-feel action bar */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button type="button" variant="outline" onClick={() => (onCancel ? onCancel() : navigate('/recruiter-dashboard'))} className="flex-1 h-11 rounded-xl" data-testid="create-student-cancel">
          Cancel
        </Button>
        <Button type="submit" disabled={loading} className="flex-1 h-11 rounded-xl bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold shadow-lg shadow-tge-navy/20" data-testid="create-student-submit">
          {loading ? 'Creating profile…' : (<>Create student profile <ChevronRight size={16} className="ml-1" /></>)}
        </Button>
      </div>
      {loading && (
        <div data-testid="create-student-progress">
          <div className="flex justify-between text-[11px] text-tge-muted mb-1">
            <span>
              {uploadProgress === 'compressing' && 'Optimising photos…'}
              {uploadProgress === 'uploading' && 'Uploading documents…'}
              {typeof uploadProgress === 'number' && `Uploading documents… ${uploadProgress}%`}
              {uploadProgress == null && 'Preparing…'}
            </span>
            <span>{typeof uploadProgress === 'number' ? `${uploadProgress}%` : ''}</span>
          </div>
          <div className="w-full h-1.5 bg-tge-cream rounded-full overflow-hidden">
            <div
              className="h-full bg-tge-gold transition-all duration-300"
              style={{ width: typeof uploadProgress === 'number' ? `${uploadProgress}%` : '10%' }}
            />
          </div>
        </div>
      )}
    </form>
  );

  // Review dialog rendered outside the form so submitting it doesn't fire the
  // form's own submit.
  const reviewDialog = (
    <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-student-review-dialog">
        <DialogHeader>
          <DialogTitle>Review student profile</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 text-sm">
          <p className="text-tge-navy/60 text-xs">
            Please double-check everything below. Nothing has been sent yet — click <strong>Confirm & submit</strong> to create the profile, or <strong>Edit</strong> to make changes.
          </p>
          <ReviewSection title="Personal information" rows={[
            ['Full name', formData.student_name],
            ['Email', formData.email],
            ['Phone', formData.phone],
            ['Date of birth', formData.dob],
            ['High school country', formData.high_school_country === 'Other' ? formData.high_school_country_other : formData.high_school_country],
            ['Marital status', formData.marital_status],
            ['Student status', formData.student_status],
          ]} />
          {(formData.emergency_contact_name || formData.emergency_contact_phone) && (
            <ReviewSection title="Emergency contact" rows={[
              ['Name', formData.emergency_contact_name],
              ['Relation', formData.emergency_contact_relation],
              ['Phone', formData.emergency_contact_phone],
            ]} />
          )}
          <ReviewSection title="Academic" rows={[
            ['Field of study', formData.field_of_study],
            ['Preferred location', formData.preferred_location],
            ['Qualifications', formData.qualifications],
            ['Preferred colleges', `${selectedColleges.length} selected`],
            ['Preferred programs', selectedPrograms.join(', ') || '—'],
          ]} />
          <div>
            <div className="text-[10px] font-semibold text-tge-navy/50 uppercase tracking-wider mb-2">Documents ({Object.values(documents).filter(Boolean).length})</div>
            <div className="flex flex-wrap gap-2">
              {docMeta.map((d) => (
                <span
                  key={d.key}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${documents[d.key] ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-tge-navy/40 bg-tge-cream border-tge-cream'}`}
                >
                  {documents[d.key] ? '✓' : '—'} {d.label}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={() => setReviewOpen(false)} disabled={loading} data-testid="create-student-review-edit">
            Edit
          </Button>
          <Button
            onClick={async () => { setReviewOpen(false); await performSubmit(); }}
            disabled={loading}
            className="bg-tge-navy hover:bg-tge-navy-deep text-white"
            data-testid="create-student-review-confirm"
          >
            {loading ? 'Submitting…' : 'Confirm & submit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (adminMode) return formContent;

  return (
    <div className="min-h-screen bg-tge-paper">
      <div className="bg-white/70 backdrop-blur-md border-b border-tge-navy/5">
        <div className="max-w-5xl mx-auto px-6 py-5 flex justify-between items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-tge-gold font-semibold">Recruiter portal</div>
            <h1 className="font-serif-display text-3xl text-tge-navy mt-0.5">Create student profile</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/recruiter-dashboard')} className="rounded-full">
            ← Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <Card className="rounded-3xl shadow-[0_2px_30px_rgba(27,42,107,0.05)]">
          <CardHeader>
            <CardTitle className="font-serif-display text-2xl text-tge-navy flex items-center gap-2">
              <FileText size={20} className="text-tge-gold" />
              Student application form
            </CardTitle>
          </CardHeader>
          <CardContent>{formContent}</CardContent>
        </Card>
      </div>
      {reviewDialog}
    </div>
  );
};

/* ---------- small presentational helpers (single file, no external bloat) -------- */

function Section({ icon: Icon, title, subtitle, accent = false, right, children }) {
  return (
    <section
      className={`rounded-2xl border ${accent ? 'border-tge-navy/15 bg-tge-cream-soft/60' : 'border-tge-cream bg-white'} p-5 sm:p-6 shadow-sm`}
    >
      <header className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${accent ? 'bg-tge-navy text-white' : 'bg-tge-gold/15 text-tge-navy'}`}>
          {Icon ? <Icon size={18} strokeWidth={1.8} /> : null}
        </div>
        <div className="flex-1">
          <h3 className="font-serif-display text-lg text-tge-navy leading-tight">{title}</h3>
          {subtitle && <p className="text-[12px] text-tge-muted mt-0.5 leading-relaxed">{subtitle}</p>}
        </div>
        {right}
      </header>
      <div>{children}</div>
    </section>
  );
}

function FieldGroup({ icon: Icon, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider text-tge-muted font-semibold">
        {Icon && <Icon size={12} className="text-tge-gold" />}
        {label}
      </Label>
      {children}
    </div>
  );
}

function UploadedPill({ done, total }) {
  const complete = done === total;
  return (
    <span
      className={`text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 shrink-0 ${
        complete ? 'bg-emerald-100 text-emerald-700' : 'bg-tge-cream text-tge-muted'
      }`}
    >
      {complete && <CheckCircle2 size={11} />} {done}/{total}
    </span>
  );
}

function DocSlot({ label, file, onChange, testid }) {
  const has = Boolean(file);
  return (
    <label
      className={`group relative flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${
        has ? 'border-emerald-300 bg-emerald-50/40' : 'border-tge-cream hover:border-tge-gold/50 hover:bg-tge-cream-soft/40'
      }`}
      data-testid={testid}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${has ? 'bg-emerald-500 text-white' : 'bg-tge-cream text-tge-muted'}`}>
        {has ? <CheckCircle2 size={16} /> : <Upload size={15} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-tge-navy">{label}</div>
        <div className={`text-[11px] truncate ${has ? 'text-emerald-700' : 'text-tge-muted'}`}>
          {has ? file.name : 'Click to upload (PDF/JPG/PNG)'}
        </div>
      </div>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        required
        onChange={(e) => onChange(e.target.files[0])}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />
    </label>
  );
}

function PickableRow({ checked, onChange, title, subtitle, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition ${
        checked ? 'border-tge-gold bg-tge-gold/8' : 'border-tge-cream hover:border-tge-gold/40 hover:bg-tge-cream-soft/40'
      }`}
      data-testid={testid}
    >
      <input type="checkbox" checked={checked} onChange={onChange} className="w-4 h-4 accent-tge-navy mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-tge-navy">{title}</div>
        {subtitle && <div className="text-[11px] text-tge-muted mt-0.5">{subtitle}</div>}
      </div>
    </label>
  );
}

function ReviewSection({ title, rows }) {
  return (
    <div>
      <div className="text-[10px] font-semibold text-tge-navy/50 uppercase tracking-wider mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3 bg-tge-cream-soft/40 border border-tge-navy/5 rounded-xl p-3">
        {rows.map(([label, value]) => (
          <div key={label}>
            <div className="text-[10px] text-tge-navy/40">{label}</div>
            <div className="text-[13px] text-tge-navy font-medium break-words">{value || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CreateStudentProfile;
